/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Capacitor } from "@capacitor/core";
import { SpeechRecognition } from "@capgo/capacitor-speech-recognition";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query as firestoreQuery,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { phylaByKingdom } from "../utils/taxonomy";
import { classesByPhylum } from "../utils/taxonomy";
import { ordersByClass } from "../utils/taxonomy";
import { familiesByOrder } from "../utils/taxonomy";
import { generaByFamily } from "../utils/taxonomy";

const TAXONOMY_LEVELS = [
  "Phylum",
  "Class",
  "Order",
  "Family",
  "Genus",
  "Scientific Name",
];

const OBSERVATION_LEVELS = [
  "Common Name",
  "Location",
  "Collections",
  "Recent Uploads",
];

function toParam(s) {
  return encodeURIComponent(String(s).toLowerCase().trim());
}

function titleCase(str) {
  return String(str || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatTaxon(s) {
  if (!s) return "";

  return titleCase(
    decodeURIComponent(s).replace(/_/g, " ").replace(/\s+/g, " ").trim(),
  );
}

function buildIndex() {
  // Map: phylumLower -> kingdomLower
  const phylumToKingdom = {};
  for (const [kLower, phyla] of Object.entries(phylaByKingdom)) {
    for (const phylum of phyla) {
      phylumToKingdom[toParam(phylum)] = kLower;
    }
  }

  // Map: classLower -> [{ kingdomLower, phylumLower }]
  const classContexts = {};
  for (const [phylumLower, classList] of Object.entries(classesByPhylum)) {
    const kingdomLower = phylumToKingdom[phylumLower];
    if (!kingdomLower) continue;
    for (const cls of classList) {
      const clsLower = toParam(cls);
      classContexts[clsLower] ||= [];
      classContexts[clsLower].push({ kingdomLower, phylumLower });
    }
  }

  // Map: orderLower -> [{ kingdomLower, phylumLower, classLower }]
  const orderContexts = {};
  for (const [classLower, orderList] of Object.entries(ordersByClass)) {
    const contexts = classContexts[classLower] || [];
    for (const ord of orderList) {
      const ordLower = toParam(ord);
      orderContexts[ordLower] ||= [];
      for (const ctx of contexts) {
        orderContexts[ordLower].push({ ...ctx, classLower });
      }
    }
  }

  // Map: familyLower -> [{ kingdomLower, phylumLower, classLower, orderLower }]
  const familyContexts = {};
  for (const [orderLower, familyList] of Object.entries(familiesByOrder)) {
    const oContexts = orderContexts[orderLower] || [];
    for (const fam of familyList) {
      const famLower = toParam(fam);
      familyContexts[famLower] ||= [];
      for (const ctx of oContexts) {
        familyContexts[famLower].push({ ...ctx, orderLower });
      }
    }
  }

  // Map: genusLower -> [{ kingdomLower, phylumLower, classLower, orderLower, familyLower, genusLabel }]
  const genusContexts = {};
  for (const [familyLower, genusList] of Object.entries(generaByFamily)) {
    const fContexts = familyContexts[familyLower] || [];
    for (const gen of genusList) {
      const genLower = toParam(gen);
      genusContexts[genLower] ||= [];
      for (const ctx of fContexts) {
        genusContexts[genLower].push({
          ...ctx,
          familyLower,
          genusLabel: gen,
        });
      }
    }
  }

  return {
    classContexts,
    orderContexts,
    familyContexts,
    genusContexts,
  };
}

function buildFilteredIndex(userGenera) {
  const base = buildIndex();

  const genusContexts = {};
  const familyContexts = {};
  const orderContexts = {};
  const classContexts = {};
  const phylumContexts = {};

  // FILTER GENERA FIRST
  for (const [genLower, contexts] of Object.entries(base.genusContexts)) {
    if (!userGenera.has(genLower)) continue;
    genusContexts[genLower] = contexts;
  }

  // BACK-PROPAGATE UP THE TREE

  for (const contexts of Object.values(genusContexts)) {
    for (const ctx of contexts) {
      const { familyLower, orderLower, classLower, phylumLower, kingdomLower } =
        ctx;

      // FAMILY
      familyContexts[familyLower] ||= [];
      familyContexts[familyLower].push({
        kingdomLower,
        phylumLower,
        classLower,
        orderLower,
      });

      // ORDER
      orderContexts[orderLower] ||= [];
      orderContexts[orderLower].push({ kingdomLower, phylumLower, classLower });

      // CLASS
      classContexts[classLower] ||= [];
      classContexts[classLower].push({ kingdomLower, phylumLower });

      // PHYLUM
      phylumContexts[phylumLower] ||= [];
      phylumContexts[phylumLower].push({ kingdomLower });
    }
  }

  return {
    phylumContexts,
    classContexts,
    orderContexts,
    familyContexts,
    genusContexts,
  };
}

// Compare spoken names against species actually recorded in this archive.
// No external taxonomic lookup or automatic guess at a different species.
function normalizeVoiceName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function spokenNameVariants(value) {
  const normalized = normalizeVoiceName(value);
  if (!normalized) return [];
  const withoutCommand = normalized.replace(
    /^(?:please )?(?:search for|look up|find|open|show me|take me to) (?:the )?(?:species |species card for |card for )?/,
    "",
  );
  return [...new Set([normalized, withoutCommand])].filter(Boolean);
}

function nameSimilarity(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 1;
  if (Math.abs(a.length - b.length) > Math.max(3, a.length * 0.25)) return 0;
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= b.length; j += 1) {
      current[j] = Math.min(
        current[j - 1] + 1,
        previous[j] + 1,
        previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    previous = current;
  }
  return 1 - previous[b.length] / Math.max(a.length, b.length);
}

// Chrome often converts unfamiliar Latin names into several ordinary words.
// Compare their sounds against names already recorded in the selected archive.
// These are candidate suggestions, never automatic identifications.
function compactScientificVoice(value) {
  return normalizeVoiceName(value)
    .replace(/\s+/g, "")
    .replace(/ph/g, "f")
    .replace(/ck/g, "k")
    .replace(/([bcdfghjklmnpqrstvwxyz])\1+/g, "$1");
}

function scientificVoiceSimilarity(spoken, taxon) {
  const a = compactScientificVoice(spoken);
  const b = compactScientificVoice(taxon);
  if (!a || !b) return 0;
  const spellingScore = nameSimilarity(a, b);
  const consonantScore = nameSimilarity(
    a.replace(/[aeiou]/g, ""),
    b.replace(/[aeiou]/g, ""),
  );
  return Math.max(spellingScore, spellingScore * 0.72 + consonantScore * 0.28);
}

// Try different word boundaries: "run fastest sulphur rattus" might be
// "Ramphastos sulfuratus". Both halves must sound reasonably similar.
function scientificBinomialScore(utterance, genus, epithet) {
  const words = normalizeVoiceName(utterance).split(" ").filter(Boolean);
  if (!genus || !epithet || words.length < 2 || words.length > 7) return 0;
  let best = 0;
  for (let split = 1; split < words.length; split += 1) {
    const genusScore = scientificVoiceSimilarity(words.slice(0, split).join(""), genus);
    const epithetScore = scientificVoiceSimilarity(words.slice(split).join(""), epithet);
    if (genusScore < 0.56 || epithetScore < 0.66) continue;
    best = Math.max(best, genusScore * 0.52 + epithetScore * 0.48);
  }
  return best;
}

// Search voice independently of the selected text-search category.
// An exact genus name opens the genus; an exact species name opens its card.
// Ambiguous exact names are shown as choices rather than auto-navigated.
function matchSpokenArchive(recognitions, species, genusContexts) {
  const utterances = [
    ...new Set((recognitions || []).flatMap(spokenNameVariants)),
  ];
  const candidates = [];
  const scoreNames = (names) => {
    let score = 0;
    for (const utterance of utterances) {
      for (const name of names) {
        if (utterance === name) score = 1;
        else if (utterance.length >= 5 && name.includes(utterance)) {
          score = Math.max(score, (0.84 * utterance.length) / name.length);
        } else if (name.length >= 5 && utterance.includes(name)) {
          score = Math.max(score, (0.82 * name.length) / utterance.length);
        } else if (utterance.length >= 5 && name.length >= 5) {
          score = Math.max(score, nameSimilarity(utterance, name) * 0.9);
        }
      }
    }
    return score;
  };

  for (const record of species) {
    const scientific = String(record.name || record.scientificName || "").trim();
    const scientificParts = normalizeVoiceName(scientific).split(" ").filter(Boolean);
    const genus = String(record.genus || scientificParts[0] || "").trim();
    const epithet = scientificParts.length >= 2 ? scientificParts[1] : "";
    const names = [
      ...new Set(
        [record.common, record.name, record.scientificName, record.name || record.id]
          .map(normalizeVoiceName)
          .filter(Boolean),
      ),
    ];
    let score = scoreNames(names);

    // Combine split-up speech words, then score genus and epithet separately.
    // The result is always < 1 so an approximate recognition needs a tap.
    if (genus && epithet) {
      for (const utterance of utterances) {
        score = Math.max(
          score,
          Math.min(0.98, scientificBinomialScore(utterance, genus, epithet) * 0.98),
        );
      }
    }
    if (score >= 0.68) candidates.push({ kind: "species", record, score });
  }

  for (const [key, contexts] of Object.entries(genusContexts || {})) {
    if (!contexts?.length) continue;
    const label = contexts[0].genusLabel || formatTaxon(key);
    let score = scoreNames([normalizeVoiceName(label)]);
    for (const utterance of utterances) {
      // A spoken binomial should not be mistaken for a genus-only request.
      if (utterance.split(" ").length > 3) continue;
      score = Math.max(score, scientificVoiceSimilarity(utterance, label) * 0.97);
    }
    if (score >= 0.61) candidates.push({ kind: "genus", label, contexts, score });
  }

  candidates.sort(
    (a, b) => b.score - a.score || (a.kind === "species" ? -1 : 1),
  );
  const exact = candidates.filter((candidate) => candidate.score === 1);
  const best = candidates[0]?.score || 0;
  // Avoid an unhelpful list of weak phonetic coincidences.
  const suggestions = (exact.length ? exact : candidates.filter(
    (candidate) => candidate.score >= best - 0.13,
  )).slice(0, 8);
  return {
    exact: exact.length === 1 ? exact[0] : null,
    suggestions,
  };
}

function recognizeInBrowser(language, onRecognizer) {
  const Constructor =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!Constructor)
    return Promise.reject(
      new Error("Speech recognition is unavailable in this browser."),
    );
  return new Promise((resolve, reject) => {
    const recognizer = new Constructor();
    onRecognizer?.(recognizer);
    recognizer.lang = language;
    recognizer.interimResults = false;
    recognizer.maxAlternatives = 5;
    recognizer.onresult = (event) => {
      const alternatives = Array.from(event.results?.[0] || []).map(
        (result) => result.transcript,
      );
      resolve(alternatives);
    };
    recognizer.onerror = (event) =>
      reject(new Error(event.error || "Speech recognition failed."));
    recognizer.onend = () => resolve([]);
    try {
      recognizer.start();
    } catch (error) {
      reject(error);
    }
  });
}

function VoiceSearchButton({ onClick, busy, available, compact = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      title={
        !available
          ? "Voice recognition is unavailable in this browser"
          : "Voice search: scientific name, common name, or genus"
      }
      aria-label={
        busy
          ? "Listening for a name"
          : "Voice search by scientific name, common name, or genus"
      }
      aria-busy={busy}
      className={`flex shrink-0 items-center justify-center rounded-lg border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/50 ${compact ? "h-9 w-9" : "h-9 w-9 md:h-10 md:w-10"} ${
        busy
          ? "border-[#01767A] bg-[#E0F5F1] text-[#01767A]"
          : "border-[#DDE7DF] bg-white text-[#374049] hover:border-[#A8D4C7] hover:bg-[#F7FBF8]"
      }`}
    >
      <svg
        aria-hidden="true"
        width="19"
        height="19"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="9" y="2" width="6" height="12" rx="3" />
        <path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8" />
      </svg>
    </button>
  );
}

export default function SearchModal({ open, onClose, uid, voiceOnly = false }) {
  const routerNavigate = useNavigate();
  const location = useLocation();

  const targetUID = uid || auth.currentUser?.uid;
  const basePath = uid ? `/explore/${uid}` : "";

  const adminModeActive =
    !!uid && new URLSearchParams(location.search).get("adminMode") === "true";

  const preserveAdminMode = useCallback(
    (target) => {
      if (!adminModeActive || !basePath || typeof target !== "string") {
        return target;
      }

      const isManagedExploreRoute =
        target === basePath || target.startsWith(`${basePath}/`);

      if (!isManagedExploreRoute) {
        return target;
      }

      const [pathAndQuery, hash = ""] = target.split("#");

      if (/(?:\?|&)adminMode=/.test(pathAndQuery)) {
        return target;
      }

      const separator = pathAndQuery.includes("?") ? "&" : "?";

      return `${pathAndQuery}${separator}adminMode=true${
        hash ? `#${hash}` : ""
      }`;
    },
    [adminModeActive, basePath],
  );

  const navigate = useCallback(
    (target, options) => {
      routerNavigate(preserveAdminMode(target), options);
    },
    [preserveAdminMode, routerNavigate],
  );

  const [level, setLevel] = useState("Scientific Name");
  const [query, setQuery] = useState("");
  const [selectedCollection, setSelectedCollection] = useState("");
  const [recentUploadsVisibleCount, setRecentUploadsVisibleCount] =
    useState(20);

  const recentUploadsLoadLockRef = useRef(false);

  const [speciesList, setSpeciesList] = useState([]);
  const [speciesLoaded, setSpeciesLoaded] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceMessage, setVoiceMessage] = useState("");
  const [voiceCandidates, setVoiceCandidates] = useState(null);
  const voiceAttemptRef = useRef(0);
  const voiceAutoStartedRef = useRef(false);
  const pendingVoiceResultsRef = useRef(null);
  const latestResolveVoiceResultsRef = useRef(null);
  const browserRecognizerRef = useRef(null);
  const nativeVoiceActiveRef = useRef(false);
  const compactVoiceSessionRef = useRef(null);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voiceSecondsLeft, setVoiceSecondsLeft] = useState(10);
  const [voiceRecording, setVoiceRecording] = useState(false);

  // Use native speech on Android and the browser speech API on supported web browsers.
  const browserSpeechAvailable =
    typeof window !== "undefined" &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  // The Android WebView's visible area shrinks when the software keyboard opens.
  // Keep this behavior exclusive to the installed app; browsers retain their UI.
  const isNativeAndroid = Capacitor.getPlatform() === "android";
  const searchInputRef = useRef(null);
  const [androidViewport, setAndroidViewport] = useState(null);
  const compactAndroidSearch =
    isNativeAndroid &&
    androidViewport?.keyboardOpen &&
    androidViewport.width < 768;

  useEffect(() => {
    if (!open || !isNativeAndroid) {
      setAndroidViewport(null);
      return;
    }

    const viewport = window.visualViewport;
    let initialHeight = viewport?.height ?? window.innerHeight;
    let initialWidth = viewport?.width ?? window.innerWidth;
    let frame = 0;

    const measureVisibleArea = () => {
      frame = 0;
      const height = viewport?.height ?? window.innerHeight;
      const width = viewport?.width ?? window.innerWidth;
      const top = viewport?.offsetTop ?? 0;

      // Rebaseline after a genuine orientation change, not a keyboard resize.
      if (Math.abs(initialWidth - width) > 100) {
        initialWidth = width;
        initialHeight = height;
      }
      if (height > initialHeight) initialHeight = height;

      const keyboardOpen = initialHeight - height > 140;
      setAndroidViewport((previous) => {
        if (
          previous &&
          Math.abs(previous.height - height) < 1 &&
          Math.abs(previous.top - top) < 1 &&
          Math.abs(previous.width - width) < 1 &&
          previous.keyboardOpen === keyboardOpen
        ) {
          return previous;
        }
        return { height, width, top, keyboardOpen };
      });
    };

    const scheduleMeasure = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measureVisibleArea);
    };

    scheduleMeasure();
    viewport?.addEventListener("resize", scheduleMeasure);
    viewport?.addEventListener("scroll", scheduleMeasure);
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      viewport?.removeEventListener("resize", scheduleMeasure);
      viewport?.removeEventListener("scroll", scheduleMeasure);
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [open, isNativeAndroid]);

  useEffect(() => {
    if (!open) {
      voiceAttemptRef.current += 1;
      voiceAutoStartedRef.current = false;
      pendingVoiceResultsRef.current = null;
      setVoiceBusy(false);
      setVoiceRecording(false);
      setVoiceTranscript("");
      setVoiceSecondsLeft(10);
      setVoiceMessage("");
      setVoiceCandidates(null);
      setLevel("Scientific Name");
      setQuery("");
      setSelectedCollection("");
      setRecentUploadsVisibleCount(20);
      recentUploadsLoadLockRef.current = false;
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverscroll = document.body.style.overscrollBehavior;
    const previousHtmlOverscroll =
      document.documentElement.style.overscrollBehavior;

    function onKeyDown(event) {
      if (event.key === "Escape") {
        onClose?.();
      }
    }

    // The compact voice control is a floating box, not a full-screen search modal.
    if (voiceOnly) {
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    }

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overscrollBehavior = previousBodyOverscroll;
      document.documentElement.style.overscrollBehavior =
        previousHtmlOverscroll;

      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, voiceOnly]);

  // Close and unmount must release the capture, timers, and native listeners.
  useEffect(() => {
    if (!open) return undefined;
    return () => {
      voiceAttemptRef.current += 1;
      const compact = compactVoiceSessionRef.current;
      if (compact) {
        compact.stopping = true;
        window.clearTimeout(compact.timeout);
        window.clearTimeout(compact.startupTimeout);
        window.clearInterval(compact.interval);
        try { compact.recognizer?.abort(); } catch (_) { /* already closed */ }
        compact.listeners.forEach((handle) => {
          Promise.resolve(handle.remove()).catch(() => {});
        });
        compactVoiceSessionRef.current = null;
        if (compact.native && (compact.captureStarted || compact.nativeStartRequested)) {
          Promise.resolve(SpeechRecognition.stop()).catch(() => {});
        }
      } else {
        try { browserRecognizerRef.current?.abort(); } catch (_) { /* already closed */ }
        if (nativeVoiceActiveRef.current) {
          Promise.resolve(SpeechRecognition.stop()).catch(() => {});
        }
      }
      browserRecognizerRef.current = null;
      nativeVoiceActiveRef.current = false;
    };
  }, [open]);

  // Only fetch when the modal is open AND the user might use common-name search
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    async function fetchSpecies() {
      try {
        setSpeciesLoaded(false);
        const q = firestoreQuery(collection(db, "users", targetUID, "species"));

        const snap = await getDocs(q);
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((sp) => Array.isArray(sp.forms) && sp.forms.length > 0);
        if (!cancelled) {
          setSpeciesList(data);
          setSpeciesLoaded(true);
        }
      } catch (e) {
        if (!cancelled) {
          setSpeciesList([]);
          setSpeciesLoaded(true);
        }
      }
    }

    fetchSpecies();

    return () => {
      cancelled = true;
    };
  }, [open, targetUID]);

  const idx = useMemo(() => {
    if (!speciesLoaded)
      return {
        phylumContexts: {},
        classContexts: {},
        orderContexts: {},
        familyContexts: {},
        genusContexts: {},
      };

    const userGenera = new Set(
      speciesList.map((sp) => toParam(sp.genus)).filter(Boolean),
    );

    return buildFilteredIndex(userGenera);
  }, [speciesList, speciesLoaded]);
  const openSpokenSpeciesCard = useCallback(
    (record) => {
      const genus = String(record.genus || "").trim();
      const scientific = String(
        record.name || record.scientificName || record.id || "",
      ).trim();
      const contexts = idx.genusContexts[genus ? toParam(genus) : ""] || [];
      const ctx = contexts[0];
      if (!ctx || !scientific) {
        setVoiceMessage(
          "This species was found, but its taxonomic page could not be resolved.",
        );
        return false;
      }
      navigate(
        `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${ctx.phylumLower}/class/${ctx.classLower}/order/${ctx.orderLower}/family/${ctx.familyLower}/genus/${toParam(genus)}?species=${encodeURIComponent(scientific)}`,
        { state: { openSpeciesId: record.id } },
      );
      onClose?.();
      return true;
    },
    [basePath, idx.genusContexts, navigate, onClose],
  );

  const openSpokenGenus = useCallback(
    (candidate) => {
      const ctx = candidate.contexts?.[0];
      if (!ctx) {
        setVoiceMessage(
          "The recognized genus has no available taxonomic route.",
        );
        return false;
      }
      navigate(
        `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${ctx.phylumLower}/class/${ctx.classLower}/order/${ctx.orderLower}/family/${ctx.familyLower}?genus=${encodeURIComponent(candidate.label)}`,
      );
      onClose?.();
      return true;
    },
    [basePath, navigate, onClose],
  );

  const openVoiceTarget = useCallback(
    (candidate) => {
      if (candidate.kind === "genus") return openSpokenGenus(candidate);
      return openSpokenSpeciesCard(candidate.record);
    },
    [openSpokenGenus, openSpokenSpeciesCard],
  );

  const resolveVoiceResults = useCallback((recognized) => {
    const spoken = recognized[0];
    if (!speciesLoaded) {
      pendingVoiceResultsRef.current = recognized;
      setVoiceMessage(`Heard “${spoken}”. Loading collection matches…`);
      return;
    }
    pendingVoiceResultsRef.current = null;
    const matched = matchSpokenArchive(recognized, speciesList, idx.genusContexts);
    if (matched.exact) {
      if (openVoiceTarget(matched.exact)) return;
      setVoiceCandidates([]);
      return;
    }
    setVoiceCandidates(matched.suggestions);
    setVoiceMessage(
      matched.suggestions.length
        ? `Heard “${spoken}”. Possible match in this collection (tap to confirm):`
        : `Heard “${spoken}”. No matching species or genus was found in this collection.`,
    );
  }, [speciesLoaded, speciesList, idx.genusContexts, openVoiceTarget]);

  // Recognition can finish after collection loading; always use the latest index.
  latestResolveVoiceResultsRef.current = resolveVoiceResults;
  useEffect(() => {
    if (!open || !speciesLoaded || !pendingVoiceResultsRef.current) return;
    resolveVoiceResults(pendingVoiceResultsRef.current);
  }, [open, speciesLoaded, resolveVoiceResults]);

  // The compact mobile box owns its own 10-second capture session. Keeping
  // this separate preserves the established full Search modal's speech flow.
  const stopCompactVoiceSearch = useCallback(async (reason = "manual") => {
    const session = compactVoiceSessionRef.current;
    if (!session || session.stopping) return;
    session.stopping = true;
    window.clearTimeout(session.timeout);
    window.clearTimeout(session.startupTimeout);
    window.clearInterval(session.interval);
    setVoiceRecording(false);

    if (session.native && (session.captureStarted || session.nativeStartRequested)) {
      // Some Android recognizers cache their final hypothesis separately.
      try {
        const last = await Promise.race([
          SpeechRecognition.getLastPartialResult?.(),
          new Promise((resolve) => window.setTimeout(() => resolve(null), 300)),
        ]);
        if (last?.matches?.length && !session.matches.length) {
          session.matches = last.matches;
        } else if (last?.text && !session.matches.length) {
          session.matches = [last.text];
        }
      } catch (_) { /* older plugin versions need no cached-result read */ }
      try {
        const stopped = await Promise.race([
          Promise.resolve(SpeechRecognition.stop()).then(() => true),
          new Promise((resolve) => window.setTimeout(() => resolve(false), 550)),
        ]);
        if (!stopped && typeof SpeechRecognition.forceStop === "function") {
          await Promise.race([
            SpeechRecognition.forceStop({ timeout: 650 }),
            new Promise((resolve) => window.setTimeout(resolve, 1000)),
          ]);
        }
      } catch (_) { /* recognizer might already have stopped on silence */ }
      // Allow the recognizer to deliver its final partial result before disposal.
      await new Promise((resolve) => window.setTimeout(resolve, 130));
      nativeVoiceActiveRef.current = false;
    } else if (session.recognizer) {
      // A browser may never emit onstart (e.g. a blocked automatic launch).
      // Release it even when there was no confirmed recording session.
      try {
        if (session.captureStarted) session.recognizer.stop();
        else session.recognizer.abort();
      } catch (_) { /* recognizer already ended */ }
      if (session.captureStarted) {
        await new Promise((resolve) => window.setTimeout(resolve, 160));
      }
    }
    session.listeners.forEach((handle) => {
      Promise.resolve(handle.remove()).catch(() => {});
    });
    if (compactVoiceSessionRef.current !== session) return;
    compactVoiceSessionRef.current = null;
    browserRecognizerRef.current = null;
    setVoiceBusy(false);

    const recognized = [...new Set(session.matches.map((value) =>
      String(value || "").trim()).filter(Boolean))];
    if (recognized.length) {
      setVoiceTranscript(recognized[0]);
      latestResolveVoiceResultsRef.current?.(recognized);
    } else {
      setVoiceMessage(
        session.error || (reason === "timeout"
          ? "10-second limit reached. No words recognized. Try again."
          : reason === "manual"
            ? "Recording stopped. No words recognized. Try again."
            : "No words recognized. Try again."),
      );
    }
  }, []);

  const startCompactVoiceSearch = useCallback(async () => {
    if (compactVoiceSessionRef.current) return;
    if (!isNativeAndroid && !browserSpeechAvailable) {
      setVoiceMessage("Voice recognition is unavailable here. Try Chrome or the BioIndex Android app.");
      return;
    }
    const session = {
      native: isNativeAndroid,
      recognizer: null,
      listeners: [],
      captureStarted: false,
      nativeStartRequested: false,
      stopping: false,
      matches: [],
      timeout: null,
      startupTimeout: null,
      interval: null,
      startedAt: 0,
      error: "",
    };
    compactVoiceSessionRef.current = session;
    voiceAttemptRef.current += 1;
    setVoiceBusy(true);
    setVoiceRecording(false);
    setVoiceTranscript("");
    setVoiceSecondsLeft(10);
    setVoiceCandidates(null);
    setVoiceMessage("Starting microphone…");
    searchInputRef.current?.blur();

    const updateTranscript = (matches) => {
      if (compactVoiceSessionRef.current !== session) return;
      const valid = (matches || []).map((text) => String(text || "").trim()).filter(Boolean);
      if (!valid.length) return;
      session.matches = valid;
      setVoiceTranscript(valid[0]);
    };
    const startCountdown = () => {
      if (session.startedAt || session.stopping || compactVoiceSessionRef.current !== session) return;
      session.startedAt = Date.now();
      session.captureStarted = true;
      setVoiceRecording(true);
      setVoiceMessage("Listening for a scientific name, common name, or genus…");
      session.interval = window.setInterval(() => {
        const remaining = Math.max(0, Math.ceil((10000 - (Date.now() - session.startedAt)) / 1000));
        setVoiceSecondsLeft(remaining);
      }, 200);
      session.timeout = window.setTimeout(() => stopCompactVoiceSearch("timeout"), 10000);
    };

    try {
      const language = navigator.language || "en-US";
      if (session.native) {
        const availability = await SpeechRecognition.available();
        if (!availability.available) throw new Error("Speech recognition is unavailable on this device.");
        const permission = await SpeechRecognition.checkPermissions();
        if (permission.speechRecognition !== "granted") {
          const granted = await SpeechRecognition.requestPermissions();
          if (granted.speechRecognition !== "granted") {
            throw new Error("Microphone permission is required for voice search.");
          }
        }
        if (session.stopping || compactVoiceSessionRef.current !== session) return;
        const partial = await SpeechRecognition.addListener("partialResults", (event) => {
          updateTranscript(event.matches?.length ? event.matches : [event.accumulatedText || event.accumulated]);
        });
        if (session.stopping || compactVoiceSessionRef.current !== session) {
          Promise.resolve(partial.remove()).catch(() => {});
          return;
        }
        session.listeners.push(partial);
        const state = await SpeechRecognition.addListener("listeningState", (event) => {
          if (session.stopping || compactVoiceSessionRef.current !== session) return;
          if (event.state === "started" || event.status === "started") startCountdown();
          if (event.state === "stopped" || event.status === "stopped") {
            stopCompactVoiceSearch("finished");
          }
        });
        if (session.stopping || compactVoiceSessionRef.current !== session) {
          Promise.resolve(state.remove()).catch(() => {});
          return;
        }
        session.listeners.push(state);
        nativeVoiceActiveRef.current = true;
        session.nativeStartRequested = true;
        const recognitionPromise = SpeechRecognition.start({
          language,
          maxResults: 5,
          popup: false,
          partialResults: true,
          prompt: "Say a scientific name, common name, or genus",
        });
        // Guarantee the limit even when a native version delays its start() promise.
        startCountdown();
        const result = await recognitionPromise;
        if (session.stopping || compactVoiceSessionRef.current !== session) return;
        updateTranscript(result?.matches);
        // With partialResults, native start() often resolves immediately;
        // recognition continues until stop(), silence, or the 10s timer.
        startCountdown();
      } else {
        const Constructor = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognizer = new Constructor();
        session.recognizer = recognizer;
        browserRecognizerRef.current = recognizer;
        recognizer.lang = language;
        recognizer.interimResults = true;
        recognizer.continuous = false;
        recognizer.maxAlternatives = 5;
        recognizer.onstart = startCountdown;
        recognizer.onresult = (event) => {
          const results = Array.from(event.results || []);
          const lastResult = results[results.length - 1];
          updateTranscript(lastResult ? Array.from(lastResult).map((item) => item.transcript) : []);
          // Final speech is complete: process it immediately instead of
          // keeping the microphone open for the rest of the ten seconds.
          if (lastResult?.isFinal && !session.stopping) {
            void stopCompactVoiceSearch("finished");
          }
        };
        recognizer.onerror = (event) => {
          if (session.stopping) return;
          session.error = /no-speech|aborted/i.test(event.error || "")
            ? "No words recognized. Try again."
            : `Microphone error: ${event.error || "recognition failed"}.`;
          stopCompactVoiceSearch("error");
        };
        recognizer.onend = () => {
          if (!session.stopping) stopCompactVoiceSearch("finished");
        };
        if (session.stopping || compactVoiceSessionRef.current !== session) return;
        recognizer.start();
        // Do not claim to be recording until Chrome actually fires onstart.
        // If automatic capture was blocked, recover with an explicit tap.
        session.startupTimeout = window.setTimeout(() => {
          if (
            compactVoiceSessionRef.current === session &&
            !session.captureStarted &&
            !session.stopping
          ) {
            session.error =
              "Microphone did not start. Tap Try again to activate it, or check Chrome's microphone permission for this site.";
            void stopCompactVoiceSearch("error");
          }
        }, 5000);
      }
    } catch (error) {
      if (session.stopping || compactVoiceSessionRef.current !== session) return;
      session.error = String(error?.message || "Unable to start voice recognition.");
      stopCompactVoiceSearch("error");
    }
  }, [isNativeAndroid, browserSpeechAvailable, stopCompactVoiceSearch]);

  const startVoiceSpeciesSearch = useCallback(async () => {
    if (voiceBusy) return;
    if (voiceOnly) {
      await startCompactVoiceSearch();
      return;
    }
    if (!isNativeAndroid && !browserSpeechAvailable) {
      setVoiceCandidates(null);
      setVoiceMessage(
        "Voice recognition is not supported in this browser. Try Chrome, Edge, or Safari.",
      );
      return;
    }
    const attempt = ++voiceAttemptRef.current;
    setVoiceBusy(true);
    setVoiceMessage("Listening for a scientific name, common name, or genus…");
    setVoiceCandidates(null);
    searchInputRef.current?.blur();
    try {
      const language = navigator.language || "en-US";
      let transcripts = [];
      if (isNativeAndroid) {
        const availability = await SpeechRecognition.available();
        if (!availability.available)
          throw new Error("Speech recognition isn't available on this device.");
        const permission = await SpeechRecognition.checkPermissions();
        if (permission.speechRecognition !== "granted") {
          const granted = await SpeechRecognition.requestPermissions();
          if (granted.speechRecognition !== "granted") {
            throw new Error(
              "Microphone permission is required for voice search.",
            );
          }
        }
        if (attempt !== voiceAttemptRef.current) return;
        nativeVoiceActiveRef.current = true;
        const result = await SpeechRecognition.start({
          language,
          maxResults: 5,
          // Avoid the native full-screen speech popup for the compact mobile flow.
          popup: !voiceOnly,
          partialResults: false,
          prompt: "Say a scientific name, common name, or genus",
        });
        nativeVoiceActiveRef.current = false;
        transcripts = result.matches || [];
      } else {
        transcripts = await recognizeInBrowser(language, (recognizer) => {
          browserRecognizerRef.current = recognizer;
        });
        browserRecognizerRef.current = null;
      }
      if (attempt !== voiceAttemptRef.current) return;

      const recognized = transcripts
        .map((value) => String(value || "").trim())
        .filter(Boolean);
      if (!recognized.length) {
        setVoiceMessage(
          "No name was recognized. Tap the microphone to try again.",
        );
        return;
      }
      latestResolveVoiceResultsRef.current?.(recognized);
    } catch (error) {
      if (attempt === voiceAttemptRef.current) {
        const message = String(error?.message || "Speech recognition failed.");
        setVoiceMessage(
          /cancel|abort|no.?speech/i.test(message)
            ? "No name was recognized. Tap the microphone to try again."
            : message,
        );
      }
    } finally {
      nativeVoiceActiveRef.current = false;
      if (attempt === voiceAttemptRef.current) setVoiceBusy(false);
    }
  }, [
    voiceBusy,
    isNativeAndroid,
    browserSpeechAvailable,
    voiceOnly,
    startCompactVoiceSearch,
  ]);

  // In React StrictMode, effects are mounted, cleaned up and mounted again.
  // Starting synchronously here allowed the first cleanup to abort Chrome's
  // recognizer while voiceAutoStartedRef stayed true, freezing "Preparing".
  // Defer the launch: the StrictMode probe cancels the first timer, and the
  // second (real) effect is allowed to start a fresh recognition session.
  useEffect(() => {
    if (!open || !voiceOnly || voiceAutoStartedRef.current) return undefined;
    const launch = window.setTimeout(() => {
      if (voiceAutoStartedRef.current) return;
      voiceAutoStartedRef.current = true;
      void startVoiceSpeciesSearch();
    }, 0);
    return () => window.clearTimeout(launch);
  }, [open, voiceOnly, startVoiceSpeciesSearch]);

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (level === "Phylum") {
      const seen = new Set();
      const items = [];

      for (const [phylumLower, contexts] of Object.entries(
        idx.phylumContexts,
      )) {
        if (seen.has(phylumLower)) continue;
        seen.add(phylumLower);

        const ctx = contexts[0];
        if (!ctx) continue;

        items.push({
          label: formatTaxon(phylumLower),
          subtitle: formatTaxon(ctx.kingdomLower),
          onPick: () =>
            navigate(
              `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${phylumLower}`,
            ),
        });
      }

      const filtered = q
        ? items.filter((item) => (item.label || "").toLowerCase().includes(q))
        : items;

      return filtered.sort((a, b) =>
        (a.label || "").localeCompare(b.label || ""),
      );
    }

    if (level === "Class") {
      const seen = new Set();
      const items = [];

      for (const [clsLower, contexts] of Object.entries(idx.classContexts)) {
        if (seen.has(clsLower)) continue;
        seen.add(clsLower);

        const ctx = contexts[0];

        items.push({
          label: formatTaxon(clsLower),
          subtitle: `${formatTaxon(ctx.kingdomLower)} → ${formatTaxon(ctx.phylumLower)}`,
          onPick: () =>
            navigate(
              `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${ctx.phylumLower}/class/${clsLower}`,
            ),
        });
      }

      const filtered = q
        ? items.filter((i) => (i.label || "").toLowerCase().includes(q))
        : items;

      return filtered.sort((a, b) =>
        (a.label || "").localeCompare(b.label || ""),
      );
    }

    if (level === "Order") {
      const seen = new Set();
      const items = [];

      for (const [orderLower, contexts] of Object.entries(idx.orderContexts)) {
        if (seen.has(orderLower)) continue;
        seen.add(orderLower);

        const ctx = contexts[0];

        items.push({
          label: formatTaxon(orderLower),
          subtitle: `${formatTaxon(ctx.kingdomLower)} → ${formatTaxon(ctx.phylumLower)} → ${formatTaxon(ctx.classLower)}`,
          onPick: () =>
            navigate(
              `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${ctx.phylumLower}/class/${ctx.classLower}/order/${orderLower}`,
            ),
        });
      }

      const filtered = q
        ? items.filter((i) => i.label.toLowerCase().includes(q))
        : items;

      return filtered.sort((a, b) => a.label.localeCompare(b.label));
    }

    if (level === "Family") {
      const seen = new Set();
      const items = [];

      for (const [famLower, contexts] of Object.entries(idx.familyContexts)) {
        if (seen.has(famLower)) continue;
        seen.add(famLower);

        const ctx = contexts[0];

        items.push({
          label: formatTaxon(famLower),
          subtitle: `${formatTaxon(ctx.kingdomLower)} → ${formatTaxon(ctx.phylumLower)} → ${formatTaxon(ctx.classLower)} → ${formatTaxon(ctx.orderLower)}`,
          onPick: () =>
            navigate(
              `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${ctx.phylumLower}/class/${ctx.classLower}/order/${ctx.orderLower}/family/${famLower}`,
            ),
        });
      }

      const filtered = q
        ? items.filter((i) => (i.label || "").toLowerCase().includes(q))
        : items;

      return filtered.sort((a, b) =>
        (a.label || "").localeCompare(b.label || ""),
      );
    }

    if (level === "Scientific Name") {
      if (!speciesLoaded) return [];

      const items = speciesList
        .map((sp) => {
          const scientific = String(
            sp.name || sp.scientificName || sp.id || "",
          ).trim();
          const common = String(sp.common || "").trim();
          const genus = String(sp.genus || "").trim();
          if (!scientific || !genus) return null;
          const contexts = idx.genusContexts[toParam(genus)] || [];
          const ctx = contexts[0];
          if (!ctx) return null;
          return {
            label: scientific,
            subtitle: common || null,
            isScientific: true,
            onPick: () =>
              navigate(
                `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${ctx.phylumLower}/class/${ctx.classLower}/order/${ctx.orderLower}/family/${ctx.familyLower}/genus/${toParam(genus)}?species=${encodeURIComponent(scientific)}`,
                { state: { openSpeciesId: sp.id } },
              ),
          };
        })
        .filter(Boolean);

      return (
        q
          ? items.filter((item) =>
              normalizeVoiceName(item.label).includes(normalizeVoiceName(q)),
            )
          : items
      ).sort((a, b) => a.label.localeCompare(b.label));
    }

    if (level === "Common Name") {
      if (!speciesLoaded) return [];

      const items = speciesList
        .filter((sp) => String(sp.common || "").trim().length > 0) // ONLY docs with common name
        .map((sp) => {
          const common = String(sp.common || "").trim();
          const sci = String(sp.name || sp.id || "").trim();
          const genus = String(sp.genus || "").trim();

          // Use your taxonomy index to locate a path for this genus
          const genLower = genus ? toParam(genus) : null;
          const contexts = genLower ? idx.genusContexts[genLower] || [] : [];
          const ctx = contexts[0] || null;

          return {
            label: common, // SEARCH TARGET
            subtitle: sci ? sci : null, // show scientific as secondary info only
            onPick: () => {
              if (!ctx) {
                navigate(basePath || `/`);
                return;
              }

              navigate(
                `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${ctx.phylumLower}/class/${ctx.classLower}/order/${ctx.orderLower}/family/${ctx.familyLower}/genus/${toParam(genus)}?species=${encodeURIComponent(sci)}`,
              );
            },
          };
        });

      const filtered = q
        ? items.filter((i) =>
            [i.label, i.subtitle]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(q),
          )
        : items;

      return filtered.sort((a, b) =>
        (a.label || "").localeCompare(b.label || ""),
      );
    }

    if (level === "Location") {
      if (!speciesLoaded) return [];

      const items = speciesList
        .flatMap((sp) => {
          const common = String(sp.common || "").trim();
          const scientific = String(sp.name || sp.id || "").trim();
          const genus = String(sp.genus || "").trim();

          const genLower = genus ? toParam(genus) : null;
          const contexts = genLower ? idx.genusContexts[genLower] || [] : [];
          const ctx = contexts[0] || null;

          const forms = Array.isArray(sp.forms) ? sp.forms : [];

          return forms.map((form, formIndex) => {
            const created =
              typeof form.createdAt?.seconds === "number"
                ? form.createdAt.seconds
                : typeof form.created === "number"
                  ? form.created
                  : 0;

            const locationName = String(
              form.locationName || form.location || "",
            ).trim();
            const locationCity = String(form.locationCity || "").trim();
            const locationAdminArea = String(
              form.locationAdminArea || "",
            ).trim();
            const locationCountry = String(form.locationCountry || "").trim();

            const fallbackLocation = [
              locationCity,
              locationAdminArea,
              locationCountry,
            ]
              .filter(Boolean)
              .filter(
                (value, index, values) =>
                  values.findIndex(
                    (item) => item.toLowerCase() === value.toLowerCase(),
                  ) === index,
              )
              .join(", ");

            const displayLocation = locationName || fallbackLocation;

            const locationSearchText = [
              locationName,
              form.location,
              locationCity,
              locationAdminArea,
              locationCountry,
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();

            const uploadDate =
              created > 0 ? new Date(created * 1000).toLocaleDateString() : "";

            return {
              label: common || scientific || "Unnamed species",
              scientific: common ? scientific : "",
              subtitle: [
                displayLocation,
                uploadDate ? `Uploaded ${uploadDate}` : "",
              ]
                .filter(Boolean)
                .join(" • "),
              image: form.url || "",
              created,
              locationSearchText,
              onPick: () => {
                if (!ctx) {
                  navigate(basePath || `/`);
                  return;
                }

                navigate(
                  `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${ctx.phylumLower}/class/${ctx.classLower}/order/${ctx.orderLower}/family/${ctx.familyLower}/genus/${toParam(genus)}?species=${encodeURIComponent(scientific)}`,
                  {
                    state: {
                      openSpeciesId: sp.id,
                      openFormKey: form.id || form.url || `form-${formIndex}`,
                    },
                  },
                );
              },
            };
          });
        })
        .filter((item) => item.image && item.locationSearchText)
        .sort((a, b) => b.created - a.created);

      return q
        ? items.filter((item) => item.locationSearchText.includes(q))
        : items;
    }

    if (level === "Collections") {
      if (!speciesLoaded) return [];

      const collectionSummary = new Map();

      for (const sp of speciesList) {
        const forms = Array.isArray(sp.forms) ? sp.forms : [];

        for (const form of forms) {
          const formCollections = Array.isArray(form.collections)
            ? form.collections
            : [];

          for (const rawCollectionName of formCollections) {
            const collectionName = String(rawCollectionName || "").trim();
            if (!collectionName) continue;

            const summary = collectionSummary.get(collectionName) || {
              photoCount: 0,
              speciesIds: new Set(),
            };

            summary.photoCount += 1;
            summary.speciesIds.add(sp.id);

            collectionSummary.set(collectionName, summary);
          }
        }
      }

      if (!selectedCollection) {
        const items = Array.from(collectionSummary.entries())
          .map(([collectionName, summary]) => ({
            label: collectionName,
            subtitle: `${summary.speciesIds.size} species • ${summary.photoCount} ${
              summary.photoCount === 1 ? "photo" : "photos"
            }`,
            isCollection: true,
            keepOpen: true,
            onPick: () => {
              setSelectedCollection(collectionName);
              setQuery("");
            },
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        return q
          ? items.filter((item) => item.label.toLowerCase().includes(q))
          : items;
      }

      const items = speciesList
        .filter((sp) => {
          const forms = Array.isArray(sp.forms) ? sp.forms : [];

          return forms.some(
            (form) =>
              Array.isArray(form.collections) &&
              form.collections.includes(selectedCollection),
          );
        })
        .map((sp) => {
          const common = String(sp.common || "").trim();
          const scientific = String(sp.name || sp.id || "").trim();
          const genus = String(sp.genus || "").trim();

          const genLower = genus ? toParam(genus) : null;
          const contexts = genLower ? idx.genusContexts[genLower] || [] : [];
          const ctx = contexts[0] || null;

          const forms = Array.isArray(sp.forms) ? sp.forms : [];
          const photoCount = forms.filter(
            (form) =>
              Array.isArray(form.collections) &&
              form.collections.includes(selectedCollection),
          ).length;

          return {
            label: common || scientific || "Unnamed species",
            scientific: common ? scientific : "",
            subtitle: `${photoCount} ${
              photoCount === 1 ? "photo" : "photos"
            } in this collection`,
            onPick: () => {
              if (!ctx) {
                navigate(basePath || `/`);
                return;
              }

              navigate(
                `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${ctx.phylumLower}/class/${ctx.classLower}/order/${ctx.orderLower}/family/${ctx.familyLower}/genus/${toParam(genus)}?species=${encodeURIComponent(scientific)}`,
              );
            },
          };
        });

      const filtered = q
        ? items.filter((item) =>
            [item.label, item.scientific]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(q),
          )
        : items;

      return filtered.sort((a, b) =>
        (a.label || "").localeCompare(b.label || ""),
      );
    }

    if (level === "Recent Uploads") {
      if (!speciesLoaded) return [];

      const items = speciesList
        .flatMap((sp) => {
          const common = String(sp.common || "").trim();
          const scientific = String(sp.name || sp.id || "").trim();
          const genus = String(sp.genus || "").trim();

          const genLower = genus ? toParam(genus) : null;
          const contexts = genLower ? idx.genusContexts[genLower] || [] : [];
          const ctx = contexts[0] || null;

          const forms = Array.isArray(sp.forms) ? sp.forms : [];

          return forms.map((form, formIndex) => {
            const created =
              typeof form.createdAt?.seconds === "number"
                ? form.createdAt.seconds
                : typeof form.created === "number"
                  ? form.created
                  : 0;

            const location = String(
              form.locationName || form.location || "",
            ).trim();

            const uploadDate =
              created > 0 ? new Date(created * 1000).toLocaleDateString() : "";

            return {
              label: common || scientific || "Unnamed species",
              scientific: common ? scientific : "",
              subtitle: [uploadDate ? `Uploaded ${uploadDate}` : "", location]
                .filter(Boolean)
                .join(" • "),
              image: form.url || "",
              created,
              onPick: () => {
                if (!ctx) {
                  navigate(basePath || `/`);
                  return;
                }

                navigate(
                  `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${ctx.phylumLower}/class/${ctx.classLower}/order/${ctx.orderLower}/family/${ctx.familyLower}/genus/${toParam(genus)}?species=${encodeURIComponent(scientific)}`,
                  {
                    state: {
                      openSpeciesId: sp.id,
                      openFormKey: form.id || form.url || `form-${formIndex}`,
                    },
                  },
                );
              },
            };
          });
        })
        .filter((item) => item.image)
        .sort((a, b) => b.created - a.created)
        .slice(0, recentUploadsVisibleCount);

      return q
        ? items.filter((item) =>
            [item.label, item.scientific, item.subtitle]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(q),
          )
        : items;
    }

    // Genus
    const seen = new Set();
    const items = [];

    for (const [genLower, contexts] of Object.entries(idx.genusContexts)) {
      if (!contexts || contexts.length === 0) continue;

      if (seen.has(genLower)) continue;
      seen.add(genLower);

      const ctx = contexts[0];
      if (!ctx) continue;

      items.push({
        label: ctx.genusLabel || formatTaxon(genLower),
        subtitle: `${formatTaxon(ctx.kingdomLower)} → ${formatTaxon(ctx.phylumLower)} → ${formatTaxon(ctx.classLower)} → ${formatTaxon(ctx.orderLower)} → ${formatTaxon(ctx.familyLower)}`,
        onPick: () =>
          navigate(
            `${basePath}/kingdom/${ctx.kingdomLower}/phylum/${ctx.phylumLower}/class/${ctx.classLower}/order/${ctx.orderLower}/family/${ctx.familyLower}?genus=${encodeURIComponent(
              ctx.genusLabel || genLower,
            )}`,
          ),
      });
    }
    const filtered = q
      ? items.filter((i) => (i.label || "").toLowerCase().includes(q))
      : items;
    return filtered.sort((a, b) =>
      (a.label || "").localeCompare(b.label || ""),
    );
  }, [
    level,
    query,
    idx,
    navigate,
    speciesList,
    speciesLoaded,
    basePath,
    selectedCollection,
    recentUploadsVisibleCount,
  ]);

  function handleResultsScroll(event) {
    if (
      level !== "Recent Uploads" ||
      recentUploadsLoadLockRef.current ||
      options.length < recentUploadsVisibleCount
    ) {
      return;
    }

    const resultsContainer = event.currentTarget;

    const reachedBottom =
      resultsContainer.scrollTop + resultsContainer.clientHeight >=
      resultsContainer.scrollHeight - 24;

    if (!reachedBottom) return;

    recentUploadsLoadLockRef.current = true;

    setRecentUploadsVisibleCount((currentCount) => currentCount + 10);

    window.setTimeout(() => {
      recentUploadsLoadLockRef.current = false;
    }, 150);
  }

  const displayedOptions =
    voiceCandidates === null
      ? options
      : voiceCandidates.map((candidate) => {
          if (candidate.kind === "genus") {
            return {
              label: candidate.label,
              subtitle: "Genus",
              isGenus: true,
              keepOpen: true,
              onPick: () => openVoiceTarget(candidate),
            };
          }
          const record = candidate.record;
          return {
            label:
              record.name || record.scientificName || record.id || "Species",
            subtitle: record.common || null,
            isScientific: true,
            keepOpen: true,
            onPick: () => openVoiceTarget(candidate),
          };
        });

  if (!open) return null;

  if (voiceOnly) {
    return (
      <div
        className="fixed inset-x-3 z-[160] mx-auto w-[min(360px,calc(100vw-24px))]"
        style={{ bottom: "calc(89px + env(safe-area-inset-bottom, 0px))" }}
      >
        <section
          aria-label="Voice search"
          className="overflow-hidden rounded-2xl border border-[#93CA87]/60 bg-white p-3.5 text-[#374049] shadow-[0_16px_45px_rgba(17,40,42,0.3)]"
        >
          <div className="flex items-center gap-3">
            <span className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${voiceBusy ? "bg-[#D8F5EE] text-[#01767A]" : "bg-[#F1F8F3] text-[#01767A]"}`}>
              {voiceBusy && <span className="absolute inset-0 animate-ping rounded-full bg-[#24C3BE]/20" aria-hidden="true" />}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="relative h-6 w-6" aria-hidden="true">
                <rect x="9" y="2" width="6" height="12" rx="3" />
                <path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8" />
              </svg>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-bold leading-tight">{voiceRecording ? "Listening…" : voiceBusy ? "Preparing microphone…" : "Voice Search"}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-[#66777B]">Say a scientific name, common name, or genus.</p>
            </div>
            <button type="button" onClick={onClose} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-[#DCE7DE] bg-white text-lg text-[#56666D]" aria-label="Close voice search">×</button>
          </div>
          {voiceBusy && (
            <div className="mt-3" aria-label={`${voiceSecondsLeft} seconds remaining`}>
              <div className="mb-1 flex items-center justify-between text-[10px] font-semibold text-[#01767A]">
                <span>{voiceRecording ? "Recording voice" : "Connecting microphone"}</span>
                <span className="tabular-nums">{voiceRecording ? `${voiceSecondsLeft}s left` : "Up to 10s"}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-[#E2EEE8]">
                <div className="h-full rounded-full bg-[#24C3BE] transition-[width] duration-200" style={{ width: `${(voiceSecondsLeft / 10) * 100}%` }} />
              </div>
            </div>
          )}
          <div className="mt-3 rounded-xl border border-[#DEEAE3] bg-[#FCFFF6] px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-[0.06em] text-[#71827C]">Words picked up</p>
            <p aria-live="polite" aria-atomic="true" className={`mt-1 min-h-[22px] break-words text-[14px] leading-snug ${voiceTranscript ? "font-semibold text-[#374049]" : "italic text-[#7A8887]"}`}>
              {voiceTranscript ? `“${voiceTranscript}”` : (voiceRecording ? "Waiting for speech…" : "No speech captured yet")}
            </p>
          </div>
          <p role="status" aria-live="polite" className="mt-2 text-[11px] leading-relaxed text-[#55726B]">
            {voiceMessage || (speciesLoaded ? "Tap the microphone to speak." : "Loading collection names…")}
          </p>
          {voiceCandidates !== null && voiceCandidates.length > 0 && (
            <div className="mt-2 max-h-48 space-y-1 overflow-y-auto border-t border-[#E3ECE7] pt-2">
              {voiceCandidates.map((candidate, index) => (
                <button
                  key={`${candidate.kind}-${index}`}
                  type="button"
                  onClick={() => openVoiceTarget(candidate)}
                  className="flex w-full flex-col rounded-lg px-3 py-2 text-left hover:bg-[#EFF9F4] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#24C3BE]"
                >
                  <span className="text-[12px] font-semibold italic text-[#374049]">
                    {candidate.kind === "genus" ? candidate.label : candidate.record.name || candidate.record.scientificName || candidate.record.id}
                  </span>
                  <span className="text-[10px] text-[#7A898C]">
                    {candidate.kind === "genus" ? "Genus" : candidate.record.common || "Species"}
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={voiceBusy ? () => stopCompactVoiceSearch("manual") : startVoiceSpeciesSearch}
              aria-label={voiceBusy ? "Stop recording" : "Start voice recognition again"}
              className={`inline-flex min-h-9 items-center gap-2 rounded-lg px-3 text-xs font-bold text-white ${voiceBusy ? "bg-[#374049] hover:bg-[#293238]" : "bg-[#01767A] hover:bg-[#015E62]"}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                <rect x="9" y="2" width="6" height="12" rx="3" />
                <path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8" />
              </svg>
              {voiceBusy ? "Stop recording" : "Try again"}
            </button>
          </div>
        </section>
      </div>
    );
  }

  const activePanel = {
    Phylum: {
      group: "Taxonomy",
      title: "Phylum",
      description: "Explore the major evolutionary divisions in this archive.",
    },
    Class: {
      group: "Taxonomy",
      title: "Class",
      description: "Move through the biological classes in this collection.",
    },
    Order: {
      group: "Taxonomy",
      title: "Order",
      description: "Explore the orders represented by recorded observations.",
    },
    Family: {
      group: "Taxonomy",
      title: "Family",
      description: "Browse the taxonomic families found in this archive.",
    },
    Genus: {
      group: "Taxonomy",
      title: "Genus",
      description: "Jump directly to a genus represented in the collection.",
    },
    "Scientific Name": {
      group: "Taxonomy",
      title: "Scientific Name",
      description: "Find a recorded species by its scientific name.",
    },
    "Common Name": {
      group: "Observations",
      title: "Common Name",
      description: "Find a recorded species using its familiar name.",
    },
    Location: {
      group: "Observations",
      title: "Location",
      description: "Find observations by place, city, region, or country.",
    },
    Collections: {
      group: "Observations",
      title: selectedCollection || "Collections",
      description: selectedCollection
        ? "Browse the species represented in this collection."
        : "Choose a collection to browse its recorded species.",
    },
    "Recent Uploads": {
      group: "Observations",
      title: "Recent Uploads",
      description: "Return to the newest observations added to this archive.",
    },
  }[level] || {
    group: "Archive",
    title: level,
    description: "Search the observations stored in this collection.",
  };

  const activeMobileGroup = OBSERVATION_LEVELS.includes(level)
    ? "Observations"
    : "Taxonomy";
  const mobileCategories =
    activeMobileGroup === "Taxonomy" ? TAXONOMY_LEVELS : OBSERVATION_LEVELS;

  const selectMobileLevel = (nextLevel) => {
    if (nextLevel === level) return;
    setLevel(nextLevel);
    setVoiceCandidates(null);
    setVoiceMessage("");
    setQuery("");
    setSelectedCollection("");
    if (nextLevel === "Recent Uploads") {
      setRecentUploadsVisibleCount(20);
      recentUploadsLoadLockRef.current = false;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50"
      style={
        isNativeAndroid && androidViewport
          ? {
              top: `${androidViewport.top}px`,
              height: `${androidViewport.height}px`,
              bottom: "auto",
            }
          : undefined
      }
    >
      <div
        className="absolute inset-0 bg-[#374049]/80 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="absolute inset-0 flex items-stretch justify-center p-0 md:items-center md:p-5">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Search BioIndex"
          className="flex h-full max-h-full w-full max-w-4xl min-h-0 flex-col overflow-hidden bg-white md:h-[calc(100dvh-2.5rem)] md:max-h-[46rem] md:rounded-[1.25rem] md:border md:border-[#DCE7DE] md:shadow-2xl"
        >
          <header
            className={`relative shrink-0 border-b border-[#E1E9E3] bg-white px-4 py-2.5 md:px-6 md:py-3.5 ${compactAndroidSearch ? "hidden" : ""}`}
          >
            {/* On mobile this is a real app bar: title and close on one line. */}
            <div className="flex items-center justify-between gap-3 md:hidden">
              <div className="flex min-w-0 items-center gap-2.5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#DCE7DE] bg-[#FCFFF6]">
                  <img
                    src="/Search.png"
                    alt=""
                    aria-hidden="true"
                    className="h-6 w-6 object-contain opacity-80"
                  />
                </div>
                <h2 className="truncate text-[21px] font-semibold tracking-[-0.025em] text-[#374049]">
                  Search
                </h2>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <VoiceSearchButton
                  onClick={startVoiceSpeciesSearch}
                  busy={voiceBusy}
                  available={isNativeAndroid || browserSpeechAvailable}
                />
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close search"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#DDE7DF] bg-white text-[#374049] transition active:bg-[#F0F8F3]"
                >
                  <svg
                    aria-hidden="true"
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Desktop: restore the search illustration and emphasize the heading. */}
            <div className="hidden items-center justify-between gap-4 md:flex">
              <div className="flex min-w-0 items-center gap-3.5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#DCE7DE] bg-[#FCFFF6]">
                  <img
                    src="/Search.png"
                    alt=""
                    aria-hidden="true"
                    className="h-7 w-7 object-contain opacity-80"
                  />
                </div>
                <h2
                  id="search-modal-title"
                  className="text-[25px] font-semibold leading-tight tracking-[-0.035em] text-[#374049]"
                >
                  Search
                </h2>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <VoiceSearchButton
                  onClick={startVoiceSpeciesSearch}
                  busy={voiceBusy}
                  available={isNativeAndroid || browserSpeechAvailable}
                />
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close search"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#DDE7DF] bg-white text-[#374049] transition hover:border-[#A8D4C7] hover:bg-[#F7FBF8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/50"
                >
                  <svg
                    aria-hidden="true"
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </header>

          <div
            className={`grid min-h-0 flex-1 grid-cols-1 ${
              compactAndroidSearch
                ? "grid-rows-[minmax(0,1fr)]"
                : "grid-rows-[auto_minmax(0,1fr)]"
            } md:grid-cols-[14.5rem_minmax(0,1fr)] md:grid-rows-1`}
          >
            {/* Mobile-only two-level navigation. One compact category row, not stacked grids. */}
            <nav
              aria-label="Search categories"
              className={`min-w-0 border-b border-[#E1E9E3] bg-[#FCFFF6] md:hidden ${compactAndroidSearch ? "hidden" : ""}`}
            >
              <div
                className="flex border-b border-[#E5ECE7] px-4"
                role="tablist"
                aria-label="Search group"
              >
                {[
                  { name: "Taxonomy", first: "Scientific Name" },
                  { name: "Observations", first: "Common Name" },
                ].map(({ name, first }) => {
                  const selected = name === activeMobileGroup;
                  return (
                    <button
                      key={name}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => selectMobileLevel(first)}
                      className={`relative flex-1 py-2.5 text-center text-[13px] transition-colors ${
                        selected
                          ? "font-semibold text-[#01767A]"
                          : "font-medium text-[#68757A]"
                      }`}
                    >
                      {name}
                      {selected && (
                        <span
                          aria-hidden="true"
                          className="absolute bottom-0 left-5 right-5 h-[2px] rounded-t-full bg-[#01767A]"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
              <div
                className={`grid gap-2 px-4 py-3 ${
                  activeMobileGroup === "Taxonomy"
                    ? "grid-cols-3"
                    : "grid-cols-2"
                }`}
                role="group"
                aria-label={`${activeMobileGroup} search categories`}
              >
                {mobileCategories.map((category) => {
                  const selected = category === level;
                  return (
                    <button
                      key={category}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => selectMobileLevel(category)}
                      className={`flex min-h-10 min-w-0 w-full items-center justify-center rounded-lg border px-2 py-2 text-center text-[12px] font-medium leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/50 ${
                        selected
                          ? "border-[#01767A] bg-[#01767A] font-semibold text-white"
                          : "border-[#DCE7DE] bg-white text-[#374049] active:border-[#83BFB7] active:bg-[#F2F9F4]"
                      }`}
                    >
                      {category === "Scientific Name" ? "Sci. Name" : category}
                    </button>
                  );
                })}
              </div>
            </nav>

            <aside
              className="hidden shrink-0 border-r border-[#E1E9E3] bg-[#FCFFF9] md:block md:min-h-0 md:overflow-y-auto md:px-4 md:py-5"
              aria-label="Search sections"
            >
              <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#748187]">
                Taxonomy
              </div>
              <div className="space-y-1.5">
                {TAXONOMY_LEVELS.map((taxonomyLevel) => {
                  const selected = taxonomyLevel === level;
                  return (
                    <button
                      key={taxonomyLevel}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => selectMobileLevel(taxonomyLevel)}
                      className={`flex min-h-10 w-full items-center rounded-lg border px-3 py-2 text-left text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/50 ${
                        selected
                          ? "border-[#01767A] bg-[#01767A] font-semibold text-white"
                          : "border-[#DCE7DE] bg-white text-[#374049] hover:border-[#A8D4C7] hover:bg-[#F2F9F4]"
                      }`}
                    >
                      {taxonomyLevel}
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 border-t border-[#E1E9E3] pt-5">
                <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#748187]">
                  Observations
                </div>
                <div className="space-y-1.5">
                  {OBSERVATION_LEVELS.map((observationLevel) => {
                    const selected = observationLevel === level;
                    return (
                      <button
                        key={observationLevel}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => selectMobileLevel(observationLevel)}
                        className={`flex min-h-10 w-full items-center rounded-lg border px-3 py-2 text-left text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/50 ${
                          selected
                            ? "border-[#01767A] bg-[#01767A] font-semibold text-white"
                            : "border-[#DCE7DE] bg-white text-[#374049] hover:border-[#A8D4C7] hover:bg-[#F2F9F4]"
                        }`}
                      >
                        {observationLevel}
                      </button>
                    );
                  })}
                </div>
              </div>
            </aside>

            <section className="flex min-h-0 flex-col bg-white">
              <div className="shrink-0 border-b border-[#E1E9E3] px-4 py-3 md:px-6 md:py-4">
                {compactAndroidSearch ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#374049]/50">
                        Search in
                      </div>
                      <div className="truncate text-base font-semibold text-[#374049]">
                        {voiceCandidates !== null
                          ? "Voice matches"
                          : activePanel.title}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <VoiceSearchButton
                        onClick={startVoiceSpeciesSearch}
                        busy={voiceBusy}
                        available={isNativeAndroid || browserSpeechAvailable}
                        compact
                      />
                      <button
                        type="button"
                        onClick={() => searchInputRef.current?.blur()}
                        className="rounded-lg border border-[#93CA87]/50 bg-[#FCFFF6] px-2.5 py-2 text-xs font-medium text-[#01767A]"
                      >
                        Change category
                      </button>
                      <button
                        type="button"
                        aria-label="Close search"
                        onClick={onClose}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#93CA87]/50 bg-white text-xl leading-none text-[#374049]"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-end justify-between gap-4">
                    <div className="min-w-0">
                      {level === "Collections" && selectedCollection && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCollection("");
                            setQuery("");
                          }}
                          className="mb-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#01767A] transition hover:text-[#24C3BE]"
                        >
                          <span aria-hidden="true">‹</span>
                          All collections
                        </button>
                      )}

                      <h3 className="text-[17px] font-semibold leading-snug tracking-[-0.015em] text-[#374049] md:text-[20px]">
                        {voiceCandidates !== null
                          ? "Voice matches"
                          : activePanel.title}
                      </h3>
                    </div>

                    {speciesLoaded && (
                      <div className="shrink-0 pb-0.5 text-xs font-medium text-[#748187] md:pb-1">
                        {displayedOptions.length}{" "}
                        {displayedOptions.length === 1 ? "result" : "results"}
                      </div>
                    )}
                  </div>
                )}

                <div className="relative mt-3">
                  <img
                    src="/Search.png"
                    alt=""
                    aria-hidden="true"
                    className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 object-contain opacity-40"
                  />

                  <input
                    ref={searchInputRef}
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setVoiceCandidates(null);
                      setVoiceMessage("");
                    }}
                    placeholder={
                      level === "Scientific Name"
                        ? "Search scientific names..."
                        : level === "Common Name"
                          ? "Search common names..."
                          : level === "Location"
                            ? "Type a place, city, region, or country..."
                            : level === "Collections"
                              ? selectedCollection
                                ? `Filter species in ${selectedCollection}...`
                                : "Filter collections..."
                              : level === "Recent Uploads"
                                ? "Filter recent uploads..."
                                : `Filter ${level}...`
                    }
                    className="h-11 w-full rounded-xl border border-[#D9E4DD] bg-[#FCFFF9] pl-11 pr-4 text-sm text-[#374049] outline-none transition placeholder:text-[#87948F] focus:border-[#24C3BE]/70 focus:bg-white focus:ring-2 focus:ring-[#24C3BE]/10"
                  />
                </div>
                {voiceMessage && (
                  <p
                    role="status"
                    className="mt-2 text-xs leading-relaxed text-[#607770]"
                  >
                    {voiceMessage}
                  </p>
                )}
              </div>

              <div
                onScroll={handleResultsScroll}
                className="min-h-0 flex-1 overflow-y-auto bg-white px-4 py-3 md:px-6 md:py-4"
              >
                {!speciesLoaded ? (
                  <div className="flex min-h-52 flex-col items-center justify-center text-center">
                    <div className="h-9 w-9 animate-spin rounded-full border-4 border-[#93CA87]/40 border-t-[#24C3BE]" />

                    <div className="mt-3 text-sm text-[#374049]/55">
                      Loading your collection…
                    </div>
                  </div>
                ) : displayedOptions.length === 0 ? (
                  <div className="flex min-h-52 flex-col items-center justify-center rounded-3xl border border-dashed border-[#93CA87]/60 bg-[#FCFFF6] px-5 text-center">
                    <h4 className="mt-3 text-base font-semibold text-[#374049] md:text-lg">
                      {voiceCandidates !== null
                        ? "No voice matches"
                        : level === "Recent Uploads" && !query.trim()
                          ? "No recent uploads"
                          : level === "Location" && query.trim()
                            ? "No matching locations"
                            : level === "Location"
                              ? "No located observations"
                              : level === "Collections" &&
                                  selectedCollection &&
                                  query.trim()
                                ? "No matching species"
                                : level === "Collections" && selectedCollection
                                  ? "No species in this collection"
                                  : level === "Collections"
                                    ? "No collections"
                                    : "No matches found"}
                    </h4>

                    <p className="mt-1 max-w-sm text-xs leading-relaxed text-[#374049]/55">
                      {voiceCandidates !== null
                        ? "Try saying the name again or use the text search."
                        : level === "Location" && query.trim()
                          ? "Try another place name, city, region, or country."
                          : level === "Collections" &&
                              selectedCollection &&
                              query.trim()
                            ? "Try another common or scientific name."
                            : level === "Collections" && !selectedCollection
                              ? "Collections will appear here when photos are assigned to them."
                              : query.trim()
                                ? "Try a different term or choose another section of the archive."
                                : "This section will grow as more observations are added."}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1.5 md:space-y-2">
                    {displayedOptions.map((o, i) => (
                      <button
                        key={`${o.label}-${i}`}
                        type="button"
                        onClick={() => {
                          if (!o.keepOpen) {
                            onClose?.();
                          }

                          o.onPick();
                        }}
                        className="group flex min-h-[54px] w-full items-center rounded-lg border border-[#E1E9E3] bg-white px-3.5 py-3 text-left transition active:border-[#A8D4C7] active:bg-[#F8FCF8] md:min-h-[58px] md:px-4 md:hover:border-[#A8D4C7] md:hover:bg-[#F8FCF8] md:focus-visible:outline-none md:focus-visible:ring-2 md:focus-visible:ring-[#24C3BE]/40"
                      >
                        <div className="flex items-center gap-3 md:w-full">
                          {o.image && level !== "Location" ? (
                            <div className="h-14 w-16 shrink-0 overflow-hidden rounded-xl border border-[#93CA87]/30 bg-slate-100 sm:h-16 sm:w-20">
                              <img
                                src={o.image}
                                alt=""
                                className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.03]"
                              />
                            </div>
                          ) : null}

                          <div className="min-w-0 flex-1">
                            <div className="text-[14px] font-medium leading-snug text-[#374049] md:text-[15px]">
                              {o.isScientific ||
                              o.isGenus ||
                              (level === "Genus" &&
                                voiceCandidates === null) ? (
                                <em>{o.label}</em>
                              ) : o.isCollection ? (
                                o.label
                              ) : [
                                  "Location",
                                  "Collections",
                                  "Recent Uploads",
                                ].includes(level) && o.scientific ? (
                                <>
                                  {o.label}{" "}
                                  <span className="italic text-[#374049]/70">
                                    ({o.scientific})
                                  </span>
                                </>
                              ) : [
                                  "Location",
                                  "Collections",
                                  "Recent Uploads",
                                ].includes(level) ? (
                                <em>{o.label}</em>
                              ) : (
                                o.label
                              )}
                            </div>

                            {o.subtitle ? (
                              <div className="mt-1 line-clamp-2 text-xs font-normal leading-relaxed text-[#748187]">
                                {level === "Common Name" ? (
                                  <em>{o.subtitle}</em>
                                ) : (
                                  o.subtitle
                                )}
                              </div>
                            ) : null}
                          </div>

                          <span
                            aria-hidden="true"
                            className="shrink-0 text-lg leading-none text-[#819B95] transition md:group-hover:text-[#01767A]"
                          >
                            ›
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
